import { useEffect, useId, useState, type FormEvent, type ReactNode } from "react";
import { CheckCircleIcon } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "./ui/dialog";
import { getJoinForm, submitJoinRequest, type JoinForm } from "../services/joinRequestService";

const focusRing =
    "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-500";
const muted = "text-slate-600 dark:text-slate-400";
const input =
    "min-h-11 w-full rounded-xl bg-white px-3 text-[15px] text-slate-900 ring-1 ring-slate-300 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500 dark:bg-zinc-900 dark:text-white dark:ring-zinc-700 dark:placeholder:text-slate-500";
const label = "mb-1.5 block text-sm font-medium text-slate-900 dark:text-white";

/** Stored as typed; the clinic and the app read it back as a label. */
const GENDERS = ["Female", "Male", "Non-binary", "Prefer not to say"];

const today = () => new Date().toISOString().slice(0, 10);

type Load = { state: "loading" } | { state: "ready"; form: JoinForm } | { state: "unavailable" };

/**
 * The Welwynn join form, in a dialog: the patient asks to join Quilt, Welwynn
 * approves it in the Quilt web app, and Quilt emails the invite code.
 *
 * While the backend has the feature off (or the clinic id is missing or
 * unknown) it shows `fallback` instead, so the page always has a way in.
 */
export function WelwynnJoinDialog({
    open,
    onOpenChange,
    clinicId,
    fallback,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    clinicId: string | null;
    fallback: ReactNode;
}) {
    const [load, setLoad] = useState<Load>({ state: "loading" });

    // Read the form once, the first time it is opened
    useEffect(() => {
        if (!open || load.state !== "loading") return;
        if (!clinicId) return setLoad({ state: "unavailable" });
        let live = true;
        getJoinForm(clinicId).then((r) => {
            if (live) setLoad(r.status === "ok" ? { state: "ready", form: r.form } : { state: "unavailable" });
        });
        return () => {
            live = false;
        };
    }, [open, clinicId, load.state]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-3xl border-slate-200 bg-white p-6 text-slate-900 sm:max-w-md md:p-8 dark:border-zinc-800 dark:bg-zinc-950 dark:text-white">
                <DialogTitle className="pr-10 text-2xl font-light">Join Quilt</DialogTitle>
                <DialogDescription className={`-mt-2 text-sm leading-relaxed ${muted}`}>
                    Tell us a little about you. Welwynn will review your request, then Quilt will email you an invite code.
                </DialogDescription>
                {load.state === "loading" && (
                    <p role="status" className={`py-8 text-center text-sm ${muted}`}>
                        Loading…
                    </p>
                )}
                {load.state === "unavailable" && fallback}
                {load.state === "ready" && <JoinForm form={load.form} />}
            </DialogContent>
        </Dialog>
    );
}

function JoinForm({ form }: { form: JoinForm }) {
    const id = useId();
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [dob, setDob] = useState("");
    const [gender, setGender] = useState("");
    const [conditions, setConditions] = useState<string[]>([]);
    const [state, setState] = useState<"idle" | "sending" | "error">("idle");
    const [error, setError] = useState("");
    const [done, setDone] = useState<string | null>(null);

    const toggle = (key: string) =>
        setConditions((cur) => (cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]));

    const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        // Hidden field that people never fill in; bots usually do
        if (new FormData(e.currentTarget).get("website")) return setDone("Thanks. Your request has been sent.");
        if (!conditions.length) {
            setError("Choose at least one option under What brings you to Quilt.");
            return setState("error");
        }
        setState("sending");
        const result = await submitJoinRequest({
            clinicId: form.clinic.id,
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            email: email.trim().toLowerCase(),
            dob: dob || undefined,
            gender: gender || undefined,
            conditions,
        });
        if (result.success) return setDone(result.message);
        setError(result.error);
        setState("error");
    };

    if (done) {
        return (
            <div role="status" className="flex items-start gap-3 rounded-2xl bg-purple-50/70 p-4 dark:bg-white/[0.04]">
                <CheckCircleIcon size={22} weight="duotone" className="mt-0.5 shrink-0 text-purple-600 dark:text-purple-400" aria-hidden="true" />
                <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-300">
                    <strong className="font-medium text-slate-900 dark:text-white">Request sent.</strong> {done}
                </p>
            </div>
        );
    }

    return (
        <form onSubmit={onSubmit} className="grid gap-4">
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label htmlFor={`${id}-first`} className={label}>
                        First name
                    </label>
                    <input id={`${id}-first`} required maxLength={100} autoComplete="given-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} className={input} />
                </div>
                <div>
                    <label htmlFor={`${id}-last`} className={label}>
                        Last name
                    </label>
                    <input id={`${id}-last`} required maxLength={100} autoComplete="family-name" value={lastName} onChange={(e) => setLastName(e.target.value)} className={input} />
                </div>
            </div>
            <div>
                <label htmlFor={`${id}-email`} className={label}>
                    Email
                </label>
                <input
                    id={`${id}-email`}
                    type="email"
                    required
                    maxLength={255}
                    autoComplete="email"
                    inputMode="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    aria-describedby={`${id}-email-note`}
                    className={input}
                />
                <p id={`${id}-email-note`} className={`mt-1.5 text-xs ${muted}`}>
                    Your invite code will be sent here.
                </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label htmlFor={`${id}-dob`} className={label}>
                        Date of birth
                    </label>
                    <input id={`${id}-dob`} type="date" required max={today()} autoComplete="bday" value={dob} onChange={(e) => setDob(e.target.value)} className={input} />
                </div>
                <div>
                    <label htmlFor={`${id}-gender`} className={label}>
                        Gender
                    </label>
                    <select id={`${id}-gender`} required value={gender} onChange={(e) => setGender(e.target.value)} className={input}>
                        <option value="" disabled>
                            Select
                        </option>
                        {GENDERS.map((g) => (
                            <option key={g} value={g}>
                                {g}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
            <fieldset>
                <legend className={label}>What brings you to Quilt?</legend>
                <p className={`-mt-0.5 mb-2 text-xs ${muted}`}>Choose all that apply.</p>
                <div className="flex flex-wrap gap-2">
                    {form.conditions.map((c) => {
                        const on = conditions.includes(c.key);
                        return (
                            <label
                                key={c.key}
                                className={`inline-flex min-h-10 cursor-pointer items-center rounded-full px-4 text-sm font-medium ring-1 transition-colors focus-within:ring-2 focus-within:ring-purple-500 ${
                                    on
                                        ? "bg-purple-600 text-white ring-purple-600 dark:bg-purple-500 dark:ring-purple-500"
                                        : "bg-white text-slate-800 ring-slate-300 hover:bg-slate-50 dark:bg-zinc-900 dark:text-slate-200 dark:ring-zinc-700 dark:hover:bg-zinc-800"
                                }`}
                            >
                                <input type="checkbox" checked={on} onChange={() => toggle(c.key)} className="sr-only" />
                                {c.label}
                            </label>
                        );
                    })}
                </div>
            </fieldset>
            {/* Honeypot: hidden from people and screen readers */}
            <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute -left-[9999px] h-px w-px opacity-0" />
            {state === "error" && (
                <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                    {error}
                </p>
            )}
            <button
                type="submit"
                disabled={state === "sending"}
                className={`min-h-12 w-full rounded-full bg-purple-600 px-5 text-[15px] font-medium text-white transition-colors hover:bg-purple-700 disabled:opacity-60 dark:bg-purple-500 dark:hover:bg-purple-400 ${focusRing}`}
            >
                {state === "sending" ? "Sending…" : `Send to ${form.clinic.name}`}
            </button>
            <p className={`text-xs leading-relaxed ${muted}`}>
                {form.clinic.name} sees these details to approve your request.{" "}
                <a
                    href="https://web.medical.quilt.gg/legal/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`rounded font-medium text-purple-600 underline underline-offset-4 hover:text-purple-700 dark:text-purple-400 ${focusRing}`}
                >
                    Privacy policy
                </a>
            </p>
        </form>
    );
}
